import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

// 기존 React Native 앱과 동일한 사용자 데이터 구조
export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  plan?: 'free_trial' | 'standard' | 'pro' | 'expired';
  trialEndsAt?: any;
  subscriptionEndsAt?: any;
  cardCount?: number;
  maxCards?: number;
  isTrialActive?: boolean;
  createdAt?: any;
  updatedAt?: any;
  settings?: {
    language?: string;
    theme?: 'light' | 'dark' | 'auto';
    notifications?: boolean;
  };
}

// 사용자 서비스 클래스
export class UserService {
  // 사용자 프로필 가져오기
  static async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      console.log('🔍 [UserService] 사용자 프로필 가져오기:', userId);
      
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = { uid: userDoc.id, ...userDoc.data() } as UserProfile;
        console.log('✅ [UserService] 사용자 프로필 가져오기 성공');
        return userData;
      } else {
        console.log('⚠️ [UserService] 사용자 프로필을 찾을 수 없음');
        return null;
      }
    } catch (error) {
      console.error('❌ [UserService] 사용자 프로필 가져오기 실패:', error);
      throw error;
    }
  }

  // 사용자 프로필 가져오기 또는 생성 (새로고침 대응)
  static async getOrCreateUserProfile(userId: string, seedData?: Partial<UserProfile>): Promise<UserProfile> {
    try {
      console.log('🔄 [UserService] 사용자 프로필 가져오기 또는 생성:', userId);
      
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = { uid: userDoc.id, ...userDoc.data() } as UserProfile;
        console.log('✅ [UserService] 기존 프로필 가져오기 성공');
        return userData;
      } else {
        console.log('⚠️ [UserService] 프로필이 없어서 새로 생성');
        
        // 기본 프로필 데이터 생성
        const defaultProfile: UserProfile = {
          uid: userId,
          email: seedData?.email || '',
          displayName: seedData?.displayName,
          photoURL: seedData?.photoURL,
          plan: 'free_trial',
          cardCount: 0,
          maxCards: 1,
          isTrialActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          settings: {
            language: 'ko',
            theme: 'light',
            notifications: true
          }
        };
        
        // 프로필 생성
        await setDoc(userRef, defaultProfile);
        console.log('✅ [UserService] 새 프로필 생성 성공');
        
        return defaultProfile;
      }
    } catch (error) {
      console.error('❌ [UserService] 프로필 가져오기/생성 실패:', error);
      throw error;
    }
  }

  // 사용자 프로필 생성 또는 업데이트
  static async createOrUpdateUserProfile(userId: string, userData: Partial<UserProfile>): Promise<void> {
    try {
      console.log('✏️ [UserService] 사용자 프로필 생성/업데이트:', { userId, userData });
      
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        // 기존 프로필 업데이트
        await updateDoc(userRef, {
          ...userData,
          updatedAt: serverTimestamp(),
        });
        console.log('✅ [UserService] 사용자 프로필 업데이트 성공');
      } else {
        // 새 프로필 생성
        await setDoc(userRef, {
          uid: userId,
          ...userData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        console.log('✅ [UserService] 사용자 프로필 생성 성공');
      }
    } catch (error) {
      console.error('❌ [UserService] 사용자 프로필 생성/업데이트 실패:', error);
      throw error;
    }
  }

  // 사용자 설정 업데이트
  static async updateUserSettings(userId: string, settings: Partial<UserProfile['settings']>): Promise<void> {
    try {
      console.log('⚙️ [UserService] 사용자 설정 업데이트:', { userId, settings });
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        settings: settings,
        updatedAt: serverTimestamp(),
      });
      
      console.log('✅ [UserService] 사용자 설정 업데이트 성공');
    } catch (error) {
      console.error('❌ [UserService] 사용자 설정 업데이트 실패:', error);
      throw error;
    }
  }

  // 구독 정보 업데이트 (기존 앱과 동일한 구조)
  static async updateSubscription(userId: string, subscriptionData: {
    plan?: UserProfile['plan'];
    trialEndsAt?: any;
    subscriptionEndsAt?: any;
    cardCount?: number;
    maxCards?: number;
    isTrialActive?: boolean;
  }): Promise<void> {
    try {
      console.log('💳 [UserService] 구독 정보 업데이트:', { userId, subscriptionData });
      
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...subscriptionData,
        updatedAt: serverTimestamp(),
      });
      
      console.log('✅ [UserService] 구독 정보 업데이트 성공');
    } catch (error) {
      console.error('❌ [UserService] 구독 정보 업데이트 실패:', error);
      throw error;
    }
  }

  // 카드 수 증가
  static async incrementCardCount(userId: string): Promise<void> {
    try {
      console.log('➕ [UserService] 카드 수 증가:', userId);
      
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const currentData = userDoc.data();
        const currentCount = currentData.cardCount || 0;
        
        await updateDoc(userRef, {
          cardCount: currentCount + 1,
          updatedAt: serverTimestamp(),
        });
        
        console.log('✅ [UserService] 카드 수 증가 성공:', currentCount + 1);
      }
    } catch (error) {
      console.error('❌ [UserService] 카드 수 증가 실패:', error);
      throw error;
    }
  }

  // 카드 수 감소
  static async decrementCardCount(userId: string): Promise<void> {
    try {
      console.log('➖ [UserService] 카드 수 감소:', userId);
      
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const currentData = userDoc.data();
        const currentCount = currentData.cardCount || 0;
        
        await updateDoc(userRef, {
          cardCount: Math.max(0, currentCount - 1),
          updatedAt: serverTimestamp(),
        });
        
        console.log('✅ [UserService] 카드 수 감소 성공:', Math.max(0, currentCount - 1));
      }
    } catch (error) {
      console.error('❌ [UserService] 카드 수 감소 실패:', error);
      throw error;
    }
  }
} 